/**
 * Sidebar Component
 *
 * Main navigation sidebar for the application.
 */

import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/utils';
import { Logger, LogCategory, logNavigation } from '@/utils/logger';
import { useAuthStore, useSidebarCollapsed, useUIStore, useIsSuperAdmin } from '@/stores';
import { APP_NAME, ROUTES } from '@/constants';
import {
  LayoutDashboard,
  PhoneCall,
  FolderOpen,
  Settings,
  CreditCard,
  Users,
  Bell,
  Key,
  Shield,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// ============================================================================
// Navigation Items
// ============================================================================

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
}

const mainNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon: <LayoutDashboard className="h-5 w-5" />,
    path: ROUTES.DASHBOARD,
  },
  {
    label: 'Phone Numbers',
    icon: <PhoneCall className="h-5 w-5" />,
    path: ROUTES.PHONE_NUMBERS,
  },
  {
    label: 'Number Groups',
    icon: <FolderOpen className="h-5 w-5" />,
    path: ROUTES.PHONE_NUMBER_GROUPS,
  },
];

const settingsNavItems: NavItem[] = [
  {
    label: 'General',
    icon: <Settings className="h-5 w-5" />,
    path: ROUTES.SETTINGS,
  },
  {
    label: 'Users',
    icon: <Users className="h-5 w-5" />,
    path: ROUTES.SETTINGS_USERS,
  },
  {
    label: 'Billing',
    icon: <CreditCard className="h-5 w-5" />,
    path: ROUTES.SETTINGS_BILLING,
  },
  {
    label: 'Notifications',
    icon: <Bell className="h-5 w-5" />,
    path: ROUTES.SETTINGS_NOTIFICATIONS,
  },
  {
    label: 'Integrations',
    icon: <Key className="h-5 w-5" />,
    path: ROUTES.SETTINGS_INTEGRATIONS,
  },
  {
    label: 'Security',
    icon: <Shield className="h-5 w-5" />,
    path: ROUTES.SETTINGS_SECURITY,
  },
];

// ============================================================================
// Nav Link Component
// ============================================================================

interface NavLinkProps {
  item: NavItem;
  isCollapsed: boolean;
  isActive: boolean;
}

function NavLink({ item, isCollapsed, isActive }: NavLinkProps) {
  const location = useLocation();

  const handleClick = () => {
    logNavigation(location.pathname, item.path, 'user', { label: item.label });
  };

  return (
    <Link
      to={item.path}
      onClick={handleClick}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
        'hover:bg-slate-100',
        isActive && 'bg-primary-50 text-primary-700 font-medium',
        !isActive && 'text-slate-600',
        isCollapsed && 'justify-center px-2'
      )}
      title={isCollapsed ? item.label : undefined}
    >
      <span className={cn(isActive && 'text-primary-600')}>{item.icon}</span>
      {!isCollapsed && (
        <span className="flex-1">{item.label}</span>
      )}
      {!isCollapsed && item.badge && item.badge > 0 && (
        <span className="px-2 py-0.5 text-xs font-medium bg-danger-100 text-danger-700 rounded-full">
          {item.badge}
        </span>
      )}
    </Link>
  );
}

// ============================================================================
// Sidebar Component
// ============================================================================

export function Sidebar() {
  const location = useLocation();
  const isCollapsed = useSidebarCollapsed();
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const company = useAuthStore((state) => state.company);
  const isSuperAdmin = useIsSuperAdmin();

  Logger.debug(LogCategory.UI, 'Rendering Sidebar', 'Sidebar', {
    isCollapsed,
    currentPath: location.pathname,
  });

  const handleLogout = () => {
    Logger.info(LogCategory.AUTH, 'User initiated logout from sidebar', 'Sidebar');
    logout();
  };

  const isActivePath = (path: string): boolean => {
    if (path === ROUTES.SETTINGS) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen bg-white border-r border-slate-200 z-40',
        'flex flex-col transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className={cn(
        'h-16 flex items-center border-b border-slate-200 px-4',
        isCollapsed && 'justify-center px-2'
      )}>
        <Link to={ROUTES.DASHBOARD} className="flex items-center gap-3">
          <img
            src="/favicon.png"
            alt="RepuCall"
            className={cn(
              'rounded-lg object-contain',
              isCollapsed ? 'h-8 w-8' : 'h-9 w-9'
            )}
          />
          {!isCollapsed && (
            <span className="font-bold text-[#1F497D] text-lg truncate">{APP_NAME}</span>
          )}
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto p-3">
        <div className="space-y-1">
          {mainNavItems.map((item) => (
            <NavLink
              key={item.path}
              item={item}
              isCollapsed={isCollapsed}
              isActive={isActivePath(item.path)}
            />
          ))}
        </div>

        {/* Settings Section */}
        <div className="mt-6">
          {!isCollapsed && (
            <p className="px-3 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Settings
            </p>
          )}
          <div className="space-y-1">
            {settingsNavItems.map((item) => (
              <NavLink
                key={item.path}
                item={item}
                isCollapsed={isCollapsed}
                isActive={isActivePath(item.path)}
              />
            ))}
          </div>
        </div>

        {/* Admin Section (Super Admin Only) */}
        {isSuperAdmin && (
          <div className="mt-6">
            {!isCollapsed && (
              <p className="px-3 mb-2 text-xs font-semibold text-purple-400 uppercase tracking-wider">
                Admin
              </p>
            )}
            <Link
              to={ROUTES.ADMIN}
              onClick={() => logNavigation(location.pathname, ROUTES.ADMIN, 'user', { label: 'Admin' })}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                'hover:bg-purple-50',
                isActivePath(ROUTES.ADMIN) && 'bg-purple-50 text-purple-700 font-medium',
                !isActivePath(ROUTES.ADMIN) && 'text-purple-600',
                isCollapsed && 'justify-center px-2'
              )}
              title={isCollapsed ? 'Admin' : undefined}
            >
              <Shield className={cn('h-5 w-5', isActivePath(ROUTES.ADMIN) && 'text-purple-600')} />
              {!isCollapsed && <span className="flex-1">Admin</span>}
            </Link>
          </div>
        )}
      </nav>

      {/* Credits Display */}
      {!isCollapsed && company && (
        <div className="p-3 mx-3 mb-3 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-slate-500">Credits</span>
            <Link
              to={ROUTES.SETTINGS_BILLING}
              className="text-xs text-primary-600 hover:text-primary-700"
            >
              Add more
            </Link>
          </div>
          <p className="text-lg font-bold text-slate-900">
            {company.credits.toLocaleString()}
          </p>
        </div>
      )}

      {/* User Info & Logout */}
      <div className={cn(
        'border-t border-slate-200 p-3',
        isCollapsed && 'flex flex-col items-center'
      )}>
        {user && (
          <div className={cn(
            'flex items-center gap-3 mb-3',
            isCollapsed && 'justify-center'
          )}>
            <div className="h-9 w-9 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-medium shrink-0">
              {user.name.charAt(0).toUpperCase()}
            </div>
            {!isCollapsed && (
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate">{user.name}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-2 rounded-lg',
            'text-slate-600 hover:bg-slate-100 transition-colors',
            isCollapsed && 'justify-center px-2'
          )}
          title={isCollapsed ? 'Log out' : undefined}
        >
          <LogOut className="h-5 w-5" />
          {!isCollapsed && <span>Log out</span>}
        </button>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => {
          Logger.debug(LogCategory.UI, 'Sidebar toggle clicked', 'Sidebar');
          toggleSidebar();
        }}
        className={cn(
          'absolute -right-3 top-20 p-1.5 bg-white border border-slate-200 rounded-full',
          'shadow-sm hover:bg-slate-50 transition-colors'
        )}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-slate-600" />
        ) : (
          <ChevronLeft className="h-4 w-4 text-slate-600" />
        )}
      </button>
    </aside>
  );
}
